import {
	Box,
	Button,
	Container,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import Pagination from '@mui/material/Pagination';
import { Placeholder, SortableTableHeader, Subheader } from 'components';
import SearchForm from 'features/trainers/SearchForm';
import TrainerItem from 'features/trainers/TrainerItem';
import TrainerToolbar from 'features/trainers/TrainerToolbar';
import { useAlerts, useNavigation, useSelected, useTrainers } from 'hooks';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const TABLE_HEADERS = [
	{
		value: 'image_url',
		label: 'Image',
		sortable: true,
		hidden: false
	},

	{
		value: 'first_name',
		label: 'First name',
		sortable: true,
		hidden: false
	},

	{
		value: 'last_name',
		label: 'Last name',
		sortable: true,
		hidden: false
	},

	{
		value: 'description',
		label: 'Description',
		sortable: true,
		hidden: true
	},

	{
		value: 'featured',
		label: 'Featured',
		sortable: true,
		hidden: false
	}
];

const TrainerList = ({ ...props }) => {
	const navigate = useNavigate();
	const {
		isLoading,
		isLoaded,
		isEmpty,
		trainers,
		findTrainers,
		deleteTrainer,
		paginate,
		query,
		page,
		numPages,
		perPage,
		totalCount,
		reloadTrainers,
		sortKey,
		sortDirection,
		handleSort
	} = useTrainers({});

	const { handleClick, handleEditClick, handleAddClick } = useNavigation({
		history,
		url: '/trainers'
	});

	const {
		selected,
		selectedIds,
		handleSelect,
		handleClear,
		handleSelectAll,
		totalSelected
	} = useSelected();

	const { showAlertSuccess, showAlertError } = useAlerts();

	const handleDeleteClick = async (trainer) => {
		await deleteTrainer(trainer);
		showAlertSuccess('Trainer has been deleted');
		reloadTrainers();
	};

	useEffect(() => {
		findTrainers(
			{
				...query,
				sort_key: sortKey,
				sort_direction: sortDirection
			},
			page
		);
	}, [query, page, sortKey, sortDirection]);

	return (
		<>
			<Subheader
				title="Trainers"
				buttons={[
					<Button
						variant="contained"
						startIcon={<Add />}
						onClick={handleAddClick}
						color="primary"
					>
						Trainer
					</Button>
				]}
			/>
			<Container maxWidth="lg">
				<>
					<Paper>
						<SearchForm
							isLoading={isLoading}
							query={query}
							handleSearch={findTrainers}
						/>
						{!isEmpty ? (
							<TableContainer component={Paper} elevation={0}>
								<Table>
									<TableHead>
										<TableRow>
											{TABLE_HEADERS.filter((h) => !h.hidden).map(
												(header, idx) => (
													<SortableTableHeader
														key={idx}
														handleSort={handleSort}
														value={`trainers.${header.value}`}
														sortable={header.sortable}
														sortKey={sortKey}
														sortDirection={sortDirection}
													>
														{header.label}
													</SortableTableHeader>
												)
											)}
											<TableCell></TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{trainers.map((trainer, idx) => (
											<TrainerItem
												key={idx}
												trainer={trainer}
												isSelectable={false}
												selectedIds={selectedIds}
												handleSelect={handleSelect}
												handleClick={handleClick}
												handleDeleteClick={handleDeleteClick}
												handleEditClick={handleEditClick}
											/>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						) : (
							<Placeholder
								icon={<Search />}
								title="No trainers"
								subtitle="Please try another search"
							/>
						)}
					</Paper>
					{!isEmpty && (
						<Box my={1}>
							<Pagination
								onChange={(ev, page) => paginate(page)}
								color="primary"
								size="small"
								shape="rounded"
								page={page}
								count={numPages}
							/>
						</Box>
					)}
				</>
				<TrainerToolbar
					selected={selected}
					selectedIds={selectedIds}
					handleClear={handleClear}
					totalSelected={totalSelected}
				/>
			</Container>
		</>
	);
};

export default TrainerList;
